/**
 * Main App component - Entry point for the FunnelHub application
 *
 * This file sets up:
 * - React Router for navigation
 * - Redux store for state management
 * - API interceptors for handling requests/responses
 * - Toast notifications
 * - Dialog context provider
 * - Auto-reload functionality for new app versions
 */

import { useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import { BrowserRouter } from "react-router-dom";
import Router from "./routing/router";
import { Provider } from "react-redux";
import { store } from "./store";
import setupInterceptors from "./apiService/setupInterceptors";
// import { useBuildVersion } from "./hooks/useBuildVersion";
import AlertDialog from "./components/funnel-form/alert-dialog";
import "./App.css";
import "./components/variables.css";
import "react-toastify/dist/ReactToastify.css";
import { DialogProvider } from "./context/dialog-context";

// Initialize API interceptors with the Redux store for auth token management
setupInterceptors(store);

/**
 * Main application component that sets up the app structure
 *
 * @returns {React.Component} The complete application with routing and providers
 */
const App = () => {
  // Reference to the countdown timer element
  const counterRef = useRef();
  // State to control the visibility of the version update alert
  const [showAlert, setShowAlert] = useState(false);

  // Version checking is currently disabled
  // const { isNewVersionAvailable } = useBuildVersion();

  /**
   * Auto-reload functionality for new app versions (currently disabled)
   * When enabled, this effect:
   * 1. Checks if a new version is available
   * 2. Shows an alert to the user
   * 3. Starts a 30-second countdown
   * 4. Reloads the page automatically when countdown completes
   */
  // useEffect(() => {
  //   if (isNewVersionAvailable) {
  //     setShowAlert(true);
  //     let timeLeft = 30;
  //     const countdown = () => {
  //       if (timeLeft === -1) {
  //         window.location.reload();
  //       } else {
  //         counterRef.current.innerHTML = timeLeft;
  //         timeLeft--;
  //       }
  //     };
  //     setInterval(countdown, 1000);
  //   }
  // }, [isNewVersionAvailable]);
  /**
   * The application renders:
   * - BrowserRouter: Provides routing capabilities
   * - Provider: Makes Redux store available throughout the app
   * - DialogProvider: Provides a consistent dialog/modal system
   * - Router: The actual routing configuration
   * - ToastContainer: Notification system
   * - AlertDialog: Version update notification
   */
  return (
    <BrowserRouter>
      <Provider store={store}>
        <DialogProvider>
          {/* Main application routes */}
          <Router />

          {/* Global toast notification container */}
          <ToastContainer
            theme="dark"
            autoClose={2000}
            hideProgressBar={true}
            position="bottom-right"
          />

          {/* New version alert dialog with countdown */}
          <AlertDialog
            description={
              <p>
                There's a new version of the Application. App is going to reload
                in <span ref={counterRef}> 30</span> seconds
              </p>
            }
            show={showAlert}
            setShow={setShowAlert}
            dialogType="warning"
            title="Warning"
            showFooter={false}
          />
        </DialogProvider>
      </Provider>
    </BrowserRouter>
  );
};
export default App;
